import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { Save } from "@mui/icons-material";

interface AddressObject {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  county: string;
  country: string;
  postcode: string;
}

interface AddressSectionProps {
  address: string;
  addressObject: AddressObject;
  postcode: string;
  onSave: (address: string, addressObject: AddressObject, postcode: string) => Promise<void>;
}

const AddressSection: React.FC<AddressSectionProps> = ({
  address: initialAddress,
  addressObject: initialAddressObject,
  postcode: initialPostcode,
  onSave,
}) => {
  const [addressFormData, setAddressFormData] = useState<AddressObject>({
    addressLine1: initialAddressObject.addressLine1 || "",
    addressLine2: initialAddressObject.addressLine2 || "",
    addressLine3: initialAddressObject.addressLine3 || "",
    county: initialAddressObject.county || "",
    country: initialAddressObject.country || "",
    postcode: initialPostcode || initialAddressObject.postcode || "",
  });

  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    setAddressFormData({
      addressLine1: initialAddressObject.addressLine1 || "",
      addressLine2: initialAddressObject.addressLine2 || "",
      addressLine3: initialAddressObject.addressLine3 || "",
      county: initialAddressObject.county || "",
      country: initialAddressObject.country || "",
      postcode: initialPostcode || initialAddressObject.postcode || "",
    });
  }, [initialAddressObject, initialPostcode]);

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Create the full address string
      const addressParts = [
        addressFormData.addressLine1,
        addressFormData.addressLine2,
        addressFormData.addressLine3,
        addressFormData.county,
        addressFormData.country,
        addressFormData.postcode,
      ].filter((part) => part.trim() !== "");

      const fullAddress = addressParts.join(", ");

      await onSave(fullAddress, addressFormData, addressFormData.postcode);
      setSuccessMessage("Address updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error: any) {
      console.error("Error saving address:", error);
      setErrorMessage(error.message || "Failed to update address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return (
      addressFormData.addressLine1 !== (initialAddressObject.addressLine1 || "") ||
      addressFormData.addressLine2 !== (initialAddressObject.addressLine2 || "") ||
      addressFormData.addressLine3 !== (initialAddressObject.addressLine3 || "") ||
      addressFormData.county !== (initialAddressObject.county || "") ||
      addressFormData.country !== (initialAddressObject.country || "") ||
      addressFormData.postcode !== (initialPostcode || initialAddressObject.postcode || "")
    );
  };

  return (
    <Box>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          fullWidth
          label="Address Line 1"
          value={addressFormData.addressLine1}
          onChange={(e) =>
            setAddressFormData({ ...addressFormData, addressLine1: e.target.value })
          }
          placeholder="Street address, P.O. box, company name"
          disabled={saving}
        />

        <TextField
          fullWidth
          label="Address Line 2"
          value={addressFormData.addressLine2}
          onChange={(e) =>
            setAddressFormData({ ...addressFormData, addressLine2: e.target.value })
          }
          placeholder="Apartment, suite, unit, building, floor, etc."
          disabled={saving}
        />

        <TextField
          fullWidth
          label="Address Line 3"
          value={addressFormData.addressLine3}
          onChange={(e) =>
            setAddressFormData({ ...addressFormData, addressLine3: e.target.value })
          }
          placeholder="Additional address information"
          disabled={saving}
        />

        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
          <TextField
            fullWidth
            label="County/State/Province"
            value={addressFormData.county}
            onChange={(e) =>
              setAddressFormData({ ...addressFormData, county: e.target.value })
            }
            placeholder="County or state"
            disabled={saving}
          />

          <TextField
            fullWidth
            label="Country"
            value={addressFormData.country}
            onChange={(e) =>
              setAddressFormData({ ...addressFormData, country: e.target.value })
            }
            placeholder="Country"
            disabled={saving}
          />
        </Box>

        <TextField
          fullWidth
          label="Postcode/ZIP"
          value={addressFormData.postcode}
          onChange={(e) =>
            setAddressFormData({ ...addressFormData, postcode: e.target.value })
          }
          placeholder="Postcode or ZIP"
          disabled={saving}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving || !hasChanges()}
        >
          {saving ? "Saving..." : "Save Address"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddressSection;


